import React from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Search, Calendar, Layers, Package } from 'lucide-react';
import { DATE_FILTER_OPTIONS, BOOKING_SOURCES } from '../../utils/bookingFormatters';
import { useAuth } from '../../contexts/AuthContext';

export function BookingsFilterBar({
  searchTerm, setSearchTerm,
  dateFilter, setDateFilter,
  filters, setFilters,
  venues, allGames, courtOptions,
  bulkMode, setBulkMode,
  showMerged, setShowMerged,
}) {
  const { isStaff } = useAuth();
  // Staff role cannot pick "This Year" or a custom date range — those
  // options are stripped from the dropdown so bookings stay scoped to
  // the shorter presets (today/tomorrow/this-week/this-month).
  const dateFilterOptions = React.useMemo(() => (
    isStaff
      ? DATE_FILTER_OPTIONS.filter((o) => o.value !== 'custom' && o.value !== 'this_year')
      : DATE_FILTER_OPTIONS
  ), [isStaff]);

  return (
    <Card className="p-6 border-slate-200">
      <div className="space-y-4">
        <div className="flex flex-col lg:flex-row lg:flex-wrap gap-3">
          {/* Search input */}
          <div className="relative flex-1 lg:min-w-[300px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Search player, mobile, ID or game..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>

          {/* Date filter dropdown */}
          <div className="w-full lg:w-28">
            <Select value={dateFilter} onValueChange={(value) => {
              setDateFilter(value);
              if (value !== 'custom') {
                setFilters({ ...filters, date: '' });
              }
            }}>
              <SelectTrigger data-testid="filter-date-type">
                <SelectValue placeholder="All Dates" />
              </SelectTrigger>
              <SelectContent>
                {dateFilterOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Custom Date range picker - only show when Custom is selected */}
          {dateFilter === 'custom' && (
            <>
              <div className="relative w-full lg:w-44">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <Input
                  type="date"
                  value={filters.dateFrom || ''}
                  onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                  className="pl-10"
                  placeholder="From date"
                  aria-label="From date"
                  data-testid="filter-custom-date-from"
                />
              </div>
              <div className="relative w-full lg:w-44">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <Input
                  type="date"
                  value={filters.dateTo || ''}
                  onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                  className="pl-10"
                  placeholder="To date"
                  aria-label="To date"
                  data-testid="filter-custom-date-to"
                  min={filters.dateFrom || undefined}
                />
              </div>
            </>
          )}

          {/* Venue dropdown */}
          <div className="w-full lg:w-36">
            <Select value={filters.venue || "all"} onValueChange={(value) => setFilters({ ...filters, venue: value === "all" ? "" : value })}>
              <SelectTrigger data-testid="filter-venue">
                <SelectValue placeholder="All Venues" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Venues</SelectItem>
                {venues.map((venue) => (
                  <SelectItem key={venue} value={venue}>{venue}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Game dropdown */}
          <div className="w-full lg:w-32">
            <Select value={filters.game || "all"} onValueChange={(value) => setFilters({ ...filters, game: value === "all" ? "" : value })}>
              <SelectTrigger data-testid="filter-game">
                <SelectValue placeholder="All Games" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Games</SelectItem>
                {allGames.map((game) => (
                  <SelectItem key={game} value={game}>{game}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Court dropdown */}
          <div className="w-full lg:w-32">
            <Select value={filters.court || "all"} onValueChange={(value) => setFilters({ ...filters, court: value === "all" ? "" : value })}>
              <SelectTrigger data-testid="filter-court">
                <SelectValue placeholder="All Courts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Courts</SelectItem>
                {courtOptions.map((court) => (
                  <SelectItem key={court} value={court}>{court}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Platform/Source dropdown */}
          <div className="w-full lg:w-32">
            <Select value={filters.booking_source || "all"} onValueChange={(value) => setFilters({ ...filters, booking_source: value === "all" ? "" : value })}>
              <SelectTrigger data-testid="filter-platform">
                <SelectValue placeholder="All Platforms" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Platforms</SelectItem>
                {BOOKING_SOURCES.map((source) => (
                  <SelectItem key={source} value={source}>{source}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Bulk toggle */}
          <Button
            variant={bulkMode ? "default" : "outline"}
            onClick={() => setBulkMode(!bulkMode)}
            className={`shrink-0 ${bulkMode ? 'bg-cyan-600 hover:bg-cyan-700' : ''}`}
            data-testid="bulk-toggle-button"
            title={bulkMode ? 'Showing Bulk Book entries' : 'Show Bulk Book entries'}
          >
            <Package className="w-4 h-4 mr-1" />
            Bulk
          </Button>

          {/* Merged view toggle */}
          <Button
            variant={showMerged ? "default" : "outline"}
            onClick={() => setShowMerged(!showMerged)}
            className="shrink-0"
            data-testid="toggle-merged-view"
            title={showMerged ? "Showing merged bookings (same player, same date, same court)" : "Showing individual slots"}
          >
            <Layers className="w-4 h-4 mr-1" />
            {showMerged ? "Merged" : "All Slots"}
          </Button>
        </div>
      </div>
    </Card>
  );
}
